import produce, { Draft, enableES5 } from 'immer';
import { GenericObject } from 'core/models/platform';
import { getTimeDifference } from 'src/_common/handlers/helper';
import { ActionTypes } from '../models/actions';
import {
  SYSTEM_FINISHED_SUMMARIZATION,
  USER_CLICKED_UNDO_DELETE_TEXT,
  USER_CLICKED_DELETE_TEXT,
  USER_CHANGED_INPUT_TEXT,
  USER_CHANGED_LENGTH_GAUGE,
  SYSTEM_STARTED_SUMMARIZATION,
  USER_CHANGED_MODE,
  HYDRATE_STORE_FROM_SESSION,
} from '../models/actions/names';
import { Output, BaseOutput, OutputAbs } from '../models/store';
import {
  TransportSummarizationAbsUnionGuard,
  TransportSummarizationExtUnionGuard,
} from '../guards/union';
import {
  TransformedSummarizationResultInAbs,
  TransformedSummarizationResult,
} from '../models/transformation';

enableES5();

const initial = {
  origText: '',
  origNumberOfSentences: 0,
  isAbsSummaryExist: false,
  ext: {
    sentences: {},
    sentenceSequence: [],
    scores: [],
    sortedScores: [],
    summarySentenceSequence: [],
    numberOfSentences: 0,
    id: '',
    modelId: '',
    userChangedSliderCount: 0,
    totalThinkingTime: 0,
    thinkingTimeStart: 0,
  },
};

const initState = {
  ...initial,
  previousState: {
    ...initial,
  },
};

export const outputReducer = (state: Output = initState, action: ActionTypes): Output => {
  switch (action.type) {
    case SYSTEM_STARTED_SUMMARIZATION: {
      let stateToUse = state;
      if (!action.payload.isRequillWithChangedConfig) stateToUse = initState;

      return {
        ...stateToUse,
        origText: action.payload.inputPara,
        origNumberOfSentences: action.payload.origNumberOfSentences,
      };
      break;
    }
    case SYSTEM_FINISHED_SUMMARIZATION: {
      const { summaryMode } = action.payload;
      const changeInState = { ...state };
      const isAbsType = TransportSummarizationAbsUnionGuard(action.payload);
      const isExtType = TransportSummarizationExtUnionGuard(action.payload);
      if (summaryMode === 'abs' && isAbsType) {
        const outputkey = isAbsType
          ? `abs~${(<TransformedSummarizationResultInAbs>action.payload).lengthGauge}`
          : '';
        changeInState[outputkey] = <GenericObject>(<unknown>action.payload);
        changeInState.isAbsSummaryExist = true;
        (changeInState[outputkey] as OutputAbs).totalThinkingTime = 0;
        (changeInState[outputkey] as OutputAbs).thinkingTimeStart = Date.now();
        if ((<TransformedSummarizationResultInAbs>action.payload).keywords) {
          changeInState.keywords = (<TransformedSummarizationResultInAbs>action.payload).keywords;
        }
        if ((<TransformedSummarizationResultInAbs>action.payload).outputKeywords) {
          changeInState.outputKeywords = (<TransformedSummarizationResultInAbs>(
            action.payload
          )).outputKeywords;
        }
      } else if (summaryMode === 'ext' && isExtType) {
        changeInState.ext = {
          ...(<TransformedSummarizationResult>action.payload),
          numberOfSentences: (<TransformedSummarizationResult>action.payload)
            .summarySentenceSequence.length,
          userChangedSliderCount: 0,
          totalThinkingTime: 0,
          thinkingTimeStart: Date.now(),
        };
      }
      return { ...changeInState };
    }
    case USER_CHANGED_LENGTH_GAUGE:
      if (action.payload.summaryMode === 'ext') {
        return produce(state, (draft: Draft<Output>) => {
          draft.ext.summarySentenceSequence = action.payload.summarySentenceSequence;
          draft.ext.numberOfSentences = action.payload.summarySentenceSequence.length;
          draft.ext.userChangedSliderCount += 1;
        });
      }

      /* Managing Thinking Time when abs slider value changes */
      return produce(state, (draft: Draft<Output>) => {
        if (draft[`abs~${action.payload.absCurrentValue}`]) {
          (draft[`abs~${action.payload.absCurrentValue}`] as OutputAbs).totalThinkingTime +=
            getTimeDifference(
              (draft[`abs~${action.payload.absCurrentValue}`] as OutputAbs).thinkingTimeStart
            );
        }
        if (draft[`abs~${action.payload.newValue}`]) {
          (draft[`abs~${action.payload.newValue}`] as OutputAbs).thinkingTimeStart = Date.now();
        }
      });
    // return state;

    case USER_CHANGED_INPUT_TEXT:
      if (action.payload.inputPara !== state.origText) {
        return {
          ...state,
          isAbsSummaryExist: false,
        };
      }
      return state;
    case USER_CLICKED_DELETE_TEXT: {
      const oldState = { ...state } as GenericObject;
      delete oldState.previousState;
      return { ...initState, previousState: <BaseOutput>oldState };
    }

    case USER_CLICKED_UNDO_DELETE_TEXT:
      return {
        ...state.previousState,
        previousState: initState,
      };
    case USER_CHANGED_MODE:
      /* Ext to Abs */
      if (action.payload.summaryMode === 'abs') {
        return produce(state, (draft: Draft<Output>) => {
          if (draft.ext.totalThinkingTime >= 0) {
            draft.ext.totalThinkingTime += getTimeDifference(draft.ext.thinkingTimeStart);
          }
          if (draft[`abs~${action.payload.absLengthGauge}`]) {
            (draft[`abs~${action.payload.absLengthGauge}`] as OutputAbs).thinkingTimeStart =
              Date.now();
          }
        });
      }
      /* Abs to Ext */
      return produce(state, (draft: Draft<Output>) => {
        if (draft[`abs~${action.payload.absLengthGauge}`]) {
          (draft[`abs~${action.payload.absLengthGauge}`] as OutputAbs).totalThinkingTime +=
            getTimeDifference(
              (draft[`abs~${action.payload.absLengthGauge}`] as OutputAbs).thinkingTimeStart
            );
        }
        if (draft.ext.thinkingTimeStart >= 0) {
          draft.ext.thinkingTimeStart = Date.now();
        }
      });

    case HYDRATE_STORE_FROM_SESSION: {
      try {
        if (action.payload.src === 'OUTPUT') {
          return { ...state, ...action.payload.store };
        }
        // eslint-disable-next-line no-empty
      } catch (e) {}
      return state;
    }

    default:
      return state;
  }
};
